/**
 * TestModeBadge — Wave 6.3 reusable indicator.
 *
 * Renders a small "TEST MODE" pill anywhere v3 surfaces records that may be
 * test-mode (cart cards, MaintAction cards, records-management table rows).
 *
 * Color: `var(--pcb-amber)` token when defined, falling back to inline rust
 * amber `#b87333` (per Bill 2026-04-25 binding rule — the only acceptable
 * inline color in this Wave). Never confused with the warn / danger pills
 * because the amber value is reserved for test-mode-only.
 */

import type { CSSProperties } from 'react';

export interface TestModeBadgeProps {
  /** When false the badge renders nothing (lets callers pass `cart.isTestMode`). */
  active?: boolean;
  /** Optional size — default 'sm'. */
  size?: 'sm' | 'md';
  /** Optional override for layout (e.g. add margins). */
  style?: CSSProperties;
}

export function TestModeBadge({
  active = true,
  size = 'sm',
  style,
}: TestModeBadgeProps): JSX.Element | null {
  if (!active) return null;

  const isMd = size === 'md';
  const pad = isMd ? '3px 8px' : '2px 6px';
  const fz = isMd ? 11 : 10;
  return (
    <span
      role="status"
      aria-label="Test mode record"
      title="This record was created in test mode and is not a live customer transaction."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: pad,
        fontSize: fz,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        // 0.157.3: warn-text (not --pcb-amber, a pale fill colour that was
        // ~1.3:1 on white) so the outline badge reads in all three themes.
        color: 'var(--warn-text)',
        background: 'transparent',
        border: '1px solid var(--warn-text)',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      TEST MODE
    </span>
  );
}

export default TestModeBadge;
